import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';

import { DateTools, TTableRow, IconButton, Tooltip } from '@treezor/design-system';

import { allBeneficiaryHeadersList, TAllBeneficiaryRowType } from '../../store/models/allBeneficiaries.model';

import { openModal } from '@/store/actions/core.actions';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';
import { StatusLabel } from '@/ui-kit/status-label/StatusLabel';
import { getStatusByLabel } from '@/types/colors.types';
import { BENEF_ITEM_PER_PAGE } from '@/features/beneficiaries/constants';
import {
  IBeneficiary,
  BeneficiaryModalKeys,
  IBeneficiaryModalProps,
} from '@/features/beneficiaries/store/models/beneficiary.model';
import { TDashboardBeneficiariyModal } from '@/store/models/dashboard-config.model';
import { TBeneficiaryAction } from '@/features/beneficiaries/hooks/AllBeneficiaries/useUserBeneficiariesData';
import { CellLink } from '@/ui-kit/table-filters/CellLink';

interface IAllBeneficiariesRowsProps {
  beneficiaries?: IBeneficiary[];
  isLoading?: boolean;
  actions?: TBeneficiaryAction[];
}

function getBeneficiaryType(beneficiary: IBeneficiary): TDashboardBeneficiariyModal {
  if (DashboardHelpers.isCredit(beneficiary)) return 'scte';
  if (DashboardHelpers.isB2B(beneficiary)) return 'sddrB2B';
  if (DashboardHelpers.isB2C(beneficiary)) return 'sddrB2C';
}

export function AllBeneficiariesRows({ beneficiaries = [], isLoading, actions }: IAllBeneficiariesRowsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleNavigateToProfile = useCallback(
    (beneficiary: IBeneficiary) => {
      const beneficiaryType = getBeneficiaryType(beneficiary);

      navigate(
        DashboardHelpers.generateProfilePath(beneficiary.userId, {
          beneficiaryFilter: { beneficiaryId: beneficiary.id, type: beneficiaryType === 'scte' ? 'scte' : 'sddr' },
        }),
      );
    },
    [navigate],
  );

  const handleNavigateToUser = useCallback(
    (userId: number) => {
      userId && navigate(DashboardHelpers.generateProfilePath(userId));
    },
    [navigate],
  );

  const toggleBeneficiaryModal = (beneficiary: IBeneficiary, key: 'view' | 'edit') => {
    const beneficiaryType = getBeneficiaryType(beneficiary);
    const keyModal = BeneficiaryModalKeys[beneficiaryType][key];
    dispatch(
      openModal<IBeneficiaryModalProps>(keyModal, {
        beneficiaryId: beneficiary.id,
        beneficiaryType,
        userId: beneficiary.userId,
      }),
    );
  };

  const shouldRedirect = actions.includes('redirect');

  if (isLoading)
    return RowSkeleton<TAllBeneficiaryRowType>({ headers: allBeneficiaryHeadersList, length: BENEF_ITEM_PER_PAGE });
  return beneficiaries?.map((beneficiary: IBeneficiary): TTableRow<TAllBeneficiaryRowType> => {
    const status = beneficiary.isActive ? 'ACTIVE' : 'INACTIVE';

    return {
      name: (
        <div className="flex flex-col">
          <span
            className="w-fit typo-body-semibold-small text-secondary cursor-pointer hover:text-interactive"
            onClick={() =>
              shouldRedirect ? handleNavigateToProfile(beneficiary) : toggleBeneficiaryModal(beneficiary, 'view')
            }
          >
            {beneficiary.name}
          </span>
          <span className="typo-body-regular-caption text-tertiary">#{beneficiary.id}</span>
        </div>
      ),
      isActive: (
        <StatusLabel label={DashboardHelpers.getStatusLabelTranslation(status)} status={getStatusByLabel(status)} />
      ),
      sepaCreditorIdentifier: (
        <div className="flex flex-col">
          <span className="text-secondary typo-body-regular-small">{beneficiary.sepaCreditorIdentifier}</span>
          <span className="typo-body-regular-small text-tertiary">
            {DashboardHelpers.isB2B(beneficiary)
              ? t('pages.currentProfiles.beneficiaries.tables.b2b')
              : t('pages.currentProfiles.beneficiaries.tables.core')}
          </span>
        </div>
      ),
      iban: <span className="text-secondary typo-body-regular-small">{beneficiary.metadata?.iban}</span>,
      bic: <span className="text-secondary typo-body-regular-small">{beneficiary.bic}</span>,
      createdDate: (
        <span className="text-secondary typo-body-regular-small">{DateTools.format(beneficiary.createdDate)}</span>
      ),
      ...(shouldRedirect && {
        userId: (
          <CellLink
            label={`${beneficiary.userId}`}
            testId={`userId-${beneficiary.userId}`}
            handleNavigate={() => handleNavigateToUser(beneficiary.userId)}
          />
        ),
      }),
      actions: actions.includes('edit') ? (
        <Tooltip messageContent={t('pages.currentProfiles.beneficiaries.tables.edit.button')}>
          <IconButton
            size="s"
            icon="edit"
            variant="secondary"
            onClick={() => toggleBeneficiaryModal(beneficiary, 'edit')}
            testId={`icon-btn-edit-${beneficiary.id}`}
          />
        </Tooltip>
      ) : (
        <></>
      ),
      onClick: () =>
        shouldRedirect ? handleNavigateToProfile(beneficiary) : toggleBeneficiaryModal(beneficiary, 'view'),
    };
  });
}
